/**
 * Audit Log Utility
 * Writes immutable audit records to Supabase for every sensitive action.
 * Every entry captures: who, what, when, what entity, full details.
 */
import { supabase } from '../data/database';

const SESSION_KEY = 'bigstar_session';

function getCurrentUser() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const { userData } = JSON.parse(raw);
    return userData || null;
  } catch {
    return null;
  }
}

/**
 * Write an audit log entry
 * @param {Object} params
 * @param {string} params.action - e.g. 'yard_sale.bid_won', 'yard_sale.bids_cancelled'
 * @param {string} params.entityType - e.g. 'yard_bid', 'unit', 'invoice'
 * @param {string} params.entityId - ID of the entity
 * @param {string} params.entityLabel - Human-readable label (e.g. "Mohamed - 2018 Hilux")
 * @param {Object} params.details - Full details JSON (amounts, IDs, lists)
 * @returns {Promise<string|null>} The audit log ID, or null if failed
 */
export async function writeAuditLog({ action, entityType, entityId, entityLabel, details }) {
  const user = getCurrentUser();

  const entry = {
    user_id: user?.id || null,
    user_name: user?.name || 'Unknown',
    user_role: user?.role || 'unknown',
    action,
    entity_type: entityType || null,
    entity_id: entityId || null,
    entity_label: entityLabel || null,
    details: details || {},
    user_agent: navigator.userAgent || null,
    ip_address: null, // Not available client-side without external service
  };

  // Write to Supabase
  if (supabase) {
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .insert(entry)
        .select('id')
        .single();

      if (error) {
        console.warn('Audit log write failed:', error.message);
        return null;
      }
      return data?.id || null;
    } catch (err) {
      console.warn('Audit log write error:', err.message);
      return null;
    }
  }

  // Fallback: store locally if no Supabase
  try {
    const logs = JSON.parse(localStorage.getItem('bigstar_audit_logs') || '[]');
    const localEntry = { ...entry, id: `local_${Date.now()}`, created_at: new Date().toISOString() };
    logs.unshift(localEntry);
    if (logs.length > 200) logs.length = 200;
    localStorage.setItem('bigstar_audit_logs', JSON.stringify(logs));
    return localEntry.id;
  } catch {
    return null;
  }
}

/**
 * Fetch audit logs from Supabase with filters
 * @param {Object} filters
 * @param {string} filters.action - filter by action type
 * @param {string} filters.entityType - filter by entity type
 * @param {string} filters.userId - filter by user
 * @param {string} filters.dateFrom - ISO date string
 * @param {string} filters.dateTo - ISO date string
 * @param {number} filters.limit - max results (default 50)
 * @param {number} filters.offset - pagination offset
 * @returns {Promise<{data: Array, count: number}>}
 */
export async function fetchAuditLogs(filters = {}) {
  if (!supabase) {
    // Fallback to local
    const logs = JSON.parse(localStorage.getItem('bigstar_audit_logs') || '[]');
    return { data: logs.slice(0, filters.limit || 50), count: logs.length };
  }

  try {
    let query = supabase
      .from('audit_logs')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters.action) query = query.eq('action', filters.action);
    if (filters.entityType) query = query.eq('entity_type', filters.entityType);
    if (filters.userId) query = query.eq('user_id', filters.userId);
    if (filters.dateFrom) query = query.gte('created_at', filters.dateFrom);
    if (filters.dateTo) query = query.lte('created_at', filters.dateTo);

    const limit = filters.limit || 50;
    const offset = filters.offset || 0;
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      console.warn('Audit log fetch failed:', error.message);
      return { data: [], count: 0 };
    }

    return { data: data || [], count: count || 0 };
  } catch (err) {
    console.warn('Audit log fetch error:', err.message);
    return { data: [], count: 0 };
  }
}
